import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Calendar, User, FileText, MapPin, Trash2, Receipt, Calculator, Plus, MessageSquare, Clock, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import UniversalStatusBadge from '@/components/shared/UniversalStatusBadge';
import { cn } from '@/lib/utils';
import { useNotify } from '@/hooks/useNotify';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import DetailPageHeader from '@/components/layout/DetailPageHeader';
import ProjectDocuments from '@/components/projects/ProjectDocuments';
import ProjectInvoices from '@/components/projects/ProjectInvoices';
import ProjectCostAnalysisModal from '@/components/projects/ProjectCostAnalysisModal';
import { STATUS_CONFIG, type ProjectStatus } from '@/components/projects/ProjectStatusDropdown';
import ProjectStatusHistory from '@/components/projects/ProjectStatusHistory';
import { useProjectNotes, useCreateProjectNote, useDeleteProjectNote } from '@/hooks/useProjectNotes';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import ActionBar from '@/components/shared/ActionBar';

const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [showCostAnalysis, setShowCostAnalysis] = useState(false);
  const [actualStartDate, setActualStartDate] = useState<Date | undefined>();
  const [actualEndDate, setActualEndDate] = useState<Date | undefined>();
  const [newNote, setNewNote] = useState('');
  const [showStatusNotesDialog, setShowStatusNotesDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<ProjectStatus | null>(null);
  const [statusNotes, setStatusNotes] = useState('');

  // Project notes hooks
  const { data: notes = [] } = useProjectNotes(id!);
  const createNoteMutation = useCreateProjectNote();
  const deleteNoteMutation = useDeleteProjectNote();

  const {
    data: project,
    isLoading,
    error
  } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          customers(name, last_name, emails, cell_numbers),
          estimates!projects_estimate_id_fkey(id, estimate_number, title, status)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Project not found');
      
      return data;
    },
    enabled: !!id,
  });

  // Update actual dates mutations
  const updateActualDatesMutation = useMutation({
    mutationFn: async ({ actualStartDate, actualEndDate }: { actualStartDate?: Date; actualEndDate?: Date }) => {
      if (!id) throw new Error('No project ID');
      
      const { error } = await supabase
        .from('projects')
        .update({
          actual_start_date: actualStartDate?.toISOString(),
          actual_end_date: actualEndDate?.toISOString()
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      notify.success('Actual dates updated successfully');
    },
    onError: (error) => {
      notify.error(error);
    }
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('No project ID');
      
      // First delete any project documents
      const { error: documentsError } = await supabase
        .from('project_documents')
        .delete()
        .eq('project_id', id);
      
      if (documentsError) throw documentsError;

      // Then delete the project
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      notify.success('Project deleted successfully');
      navigate('/projects');
    },
    onError: (error) => {
      notify.error(error);
    }
  });

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ newStatus, notes }: { newStatus: ProjectStatus; notes?: string }) => {
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', id);
      if (error) throw error;
      if (notes) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('project_status_history')
            .update({ notes })
            .eq('project_id', id)
            .eq('new_status', newStatus)
            .order('changed_at', { ascending: false })
            .limit(1);
        }
      }
      return newStatus;
    },
    onSuccess: (newStatus) => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projectStatusHistory', id] });
      notify.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
    },
    onError: (error) => {
      console.error('Error updating project status:', error);
      notify.error(error);
    },
  });

  const handleProjectStatusChange = (newStatus: ProjectStatus) => {
    if (newStatus === 'on_hold' || newStatus === 'cancelled' || newStatus === 'delayed') {
      setPendingStatus(newStatus);
      setShowStatusNotesDialog(true);
    } else {
      updateStatusMutation.mutate({ newStatus });
    }
  };

  const handleConfirmStatusChange = () => {
    if (pendingStatus) {
      updateStatusMutation.mutate({ newStatus: pendingStatus, notes: statusNotes });
      setShowStatusNotesDialog(false);
      setPendingStatus(null);
      setStatusNotes('');
    }
  };

  const renderEstimateStatusBadge = (status: string) => (
    <UniversalStatusBadge entityType="estimate" status={status} size="sm" />
  );

  const handleCustomerClick = () => {
    if (project?.customer_id) {
      navigate(`/customers?customer=${project.customer_id}`);
    }
  };

  const handleEstimateClick = () => {
    if (project?.estimates?.id) {
      navigate(`/estimates/${project.estimates.id}`);
    }
  };

  // Notes handlers
  const handleAddNote = () => {
    if (newNote.trim() && id) {
      createNoteMutation.mutate({
        projectId: id,
        content: newNote.trim()
      }, {
        onSuccess: () => {
          setNewNote('');
          notify.success('Note added successfully');
        },
        onError: (error) => {
          notify.error(error);
        }
      });
    }
  };

  const handleDeleteNote = (noteId: string) => {
    deleteNoteMutation.mutate(noteId, {
      onSuccess: () => {
        notify.success('Note deleted successfully');
      },
      onError: (error) => {
        notify.error(error);
      }
    });
  };

  // Initialize actual dates from project data
  React.useEffect(() => {
    if (project) {
      setActualStartDate(project.actual_start_date ? new Date(project.actual_start_date) : undefined);
      setActualEndDate(project.actual_end_date ? new Date(project.actual_end_date) : undefined);
    }
  }, [project]);

  // Get status for header badge
  const projectStatusVariantMap: Record<ProjectStatus, "default" | "secondary" | "destructive" | "outline"> = {
    planning: 'outline',
    pending_start: 'secondary',
    in_progress: 'default',
    on_hold: 'secondary',
    delayed: 'destructive',
    completed: 'default',
    cancelled: 'destructive',
  };

  const currentProjectStatus = (project?.status?.toLowerCase() || 'planning') as ProjectStatus;

  const getStatusConfig = () => {
    if (!project?.status) return undefined;
    const config = STATUS_CONFIG[currentProjectStatus];
    if (!config) return { label: project.status, variant: 'outline' as const };
    return { label: config.label, variant: projectStatusVariantMap[currentProjectStatus] };
  };

  const projectStatusOptions = (Object.keys(STATUS_CONFIG) as ProjectStatus[]).map(s => ({
    value: s,
    label: STATUS_CONFIG[s].label,
    variant: projectStatusVariantMap[s],
  }));

  if (isLoading) {
    return (
      <DetailPageLayout>
        <div className="space-y-4">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-64 bg-muted animate-pulse rounded" />
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !project) {
    return (
      <DetailPageLayout>
        <div className="text-center py-12">
          <p className="text-destructive font-semibold mb-4">
            {error instanceof Error ? error.message : 'Project not found'}
          </p>
          <Button asChild variant="outline">
            <Link to="/projects">Back to Projects</Link>
          </Button>
        </div>
      </DetailPageLayout>
    );
  }

  return (
    <DetailPageLayout>
      <DetailPageHeader
        title={project.name}
        subtitle={`Created ${format(new Date(project.created_at), 'dd MMMM yyyy')}`}
        status={getStatusConfig()}
        backLink={{ label: "Back to Projects", href: "/projects" }}
        primaryAction={{
          label: "Edit",
          icon: Edit,
          onClick: () => navigate(`/projects/edit/${id}`)
        }}
        secondaryActions={[
          {
            label: "Change Status",
            icon: RefreshCw,
            onClick: () => {},
            subActions: projectStatusOptions.map(s => ({
              label: s.label,
              onClick: () => handleProjectStatusChange(s.value),
              badge: { variant: s.variant },
              isCurrent: s.value === currentProjectStatus,
              disabled: s.value === currentProjectStatus,
            })),
          },
          {
            label: "Create Invoice",
            icon: Receipt,
            onClick: () => navigate(`/invoices/create?estimate_id=${project.estimate_id}`),
            hidden: !project.estimate_id,
          },
          {
            label: "Cost Analysis",
            icon: Calculator,
            onClick: () => setShowCostAnalysis(true)
          },
          {
            label: "Delete",
            icon: Trash2,
            onClick: () => deleteProjectMutation.mutate(),
            destructive: true,
            requiresConfirmation: {
              title: "Delete Project",
              description: `Are you sure you want to delete "${project.name}"? This action cannot be undone.`
            }
          }
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Project Details */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
               <div>
                 <h2 className="text-xl font-bold">{project.name}</h2>
                 <div className="flex items-center gap-2 mt-2">
                   <Label className="text-sm text-muted-foreground">Status:</Label>
                   <UniversalStatusBadge entityType="project" status={project.status || 'planning'} size="sm" />
                 </div>
               </div>
              
              {project.description && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Description</h3>
                  <p>{project.description}</p>
                </div>
              )}
              
              {project.customers && (
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="h-4 w-4 text-primary flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{getCustomerDisplayName(project.customers)}</div>
                        {project.customers.emails && project.customers.emails.length > 0 && (
                          <div className="text-xs text-muted-foreground truncate">{project.customers.emails[0]}</div>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCustomerClick}
                      className="w-full sm:w-auto"
                    >
                      View Customer
                    </Button>
                  </div>
                </div>
              )}

              {/* Associated Estimate */}
              {project.estimates && (
                <div className="p-4 bg-success/5 rounded-lg border border-success/20">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Receipt className="h-4 w-4 text-success flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{project.estimates.estimate_number}</div>
                        <div className="text-xs text-muted-foreground truncate">{project.estimates.title}</div>
                        <div className="mt-1">
                          {renderEstimateStatusBadge(project.estimates.status)}
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleEstimateClick}
                      className="w-full sm:w-auto"
                    >
                      View Estimate
                    </Button>
                  </div>
                </div>
              )}
              
              {project.expected_start_date && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Expected Start Date</h3>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(project.expected_start_date), 'dd MMMM yyyy')}
                  </p>
                </div>
              )}

              {project.expected_end_date && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">Expected End Date</h3>
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(project.expected_end_date), 'dd MMMM yyyy')}
                  </p>
                </div>
              )}

              {/* Actual Start Date */}
              <div>
                <Label htmlFor="actual-start-date" className="text-sm font-medium text-muted-foreground">
                  Actual Start Date
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1",
                        !actualStartDate && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {actualStartDate ? format(actualStartDate, "dd MMMM yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={actualStartDate}
                      onSelect={(date) => {
                        setActualStartDate(date);
                        updateActualDatesMutation.mutate({
                          actualStartDate: date,
                          actualEndDate
                        });
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Actual End Date */}
              <div>
                <Label htmlFor="actual-end-date" className="text-sm font-medium text-muted-foreground">
                  Actual End Date
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1",
                        !actualEndDate && "text-muted-foreground"
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {actualEndDate ? format(actualEndDate, "dd MMMM yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={actualEndDate}
                      onSelect={(date) => {
                        setActualEndDate(date);
                        updateActualDatesMutation.mutate({
                          actualStartDate,
                          actualEndDate: date
                        });
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Site Address */}
              {project.use_separate_site_address && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Site Address</h3>
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="text-sm">
                        {project.site_street_address && <div>{project.site_street_address}</div>}
                        {project.site_suburb && <div>{project.site_suburb}</div>}
                        {project.site_city && <div>{project.site_city}</div>}
                        {project.site_province_state && <div>{project.site_province_state}</div>}
                        {project.site_postal_code && <div>{project.site_postal_code}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status History */}
          <ProjectStatusHistory projectId={id!} />

          {/* Project Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Project Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new note */}
              <div className="space-y-2">
                <Label htmlFor="new-note">Add a note</Label>
                <Textarea
                  id="new-note"
                  placeholder="Enter your note here..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                />
                <Button 
                  onClick={handleAddNote}
                  disabled={!newNote.trim() || createNoteMutation.isPending}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Note
                </Button>
              </div>

              {/* Notes list */}
              <div className="space-y-3">
                {notes.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No notes yet. Add the first note above.</p>
                ) : (
                  <ScrollArea className="h-64">
                    <div className="space-y-3">
                      {notes.map((note) => (
                        <div key={note.id} className="p-3 bg-muted rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm">{note.content}</p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span><span>{format(new Date(note.created_at), 'dd MMMM yyyy HH:mm')}</span></span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteNote(note.id)}
                              disabled={deleteNoteMutation.isPending}
                              className="ml-2 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Documents and Invoices */}
        <div className="lg:col-span-2 space-y-6">
          <ProjectDocuments projectId={id!} />
          <ProjectInvoices projectId={id!} estimateId={project?.estimate_id} />
        </div>
      </div>

      {/* Cost Analysis Modal */}
      <ProjectCostAnalysisModal
        projectId={id!}
        isOpen={showCostAnalysis}
        onClose={() => setShowCostAnalysis(false)}
      />

      {/* Status Notes Dialog */}
      <Dialog open={showStatusNotesDialog} onOpenChange={setShowStatusNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Change Status to {pendingStatus ? STATUS_CONFIG[pendingStatus].label : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="status-notes">Reason for status change (optional)</Label>
              <Textarea
                id="status-notes"
                placeholder="Enter reason for this status change..."
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowStatusNotesDialog(false); setPendingStatus(null); setStatusNotes(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmStatusChange}
              disabled={updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DetailPageLayout>
  );
};

export default ProjectDetailPage;
